import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { deleteRevenueReportMutation, getRevenueReportsListQuery } from "@/graph";
import { openAlert, setRefetchRevenueReportsList } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDeleteRevenueReport = (limit, page) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [deleteRevenueReportAction, { loading }] = useMutation(deleteRevenueReportMutation, {
        awaitRefetchQueries: true,
        refetchQueries: [
            {
                query: getRevenueReportsListQuery,
                variables: {
                    limit,
                    page,
                    where: {},
                },
            },
        ],
    });

    const { deleteRevenueReportPending: deleteRevenueReportPendingToastName } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const deleteRevenueReport = async (id) => {
        try {
            await deleteRevenueReportAction({
                onCompleted: () => {
                    dispatch(setRefetchRevenueReportsList(true));

                    dispatch(
                        openAlert(
                            t("messages.deleteMsgs.success", { entity: t("revenueReports.entity") }),
                            statusTypesData.success,
                        ),
                    );
                },

                variables: {
                    where: { id },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${deleteRevenueReportPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading) {
                toast.info(() => t("messages.deleteMsgs.pending", { entity: t("labels.template") }), {
                    toastId: `${deleteRevenueReportPendingToastName}_${toastId}`,
                });
            }
        },
        [loading], //eslint-disable-line
    );

    return {
        deleteRevenueReport,
        loading,
    };
};

export default useDeleteRevenueReport;
